import React from "react";
import { motion } from "framer-motion";
import { navItems } from "../../config/navigationConfig";

/**
 * Componente de navegación reutilizable con animaciones Framer Motion
 * Se puede usar tanto en Header como en Footer
 * 
 * @param {Object} props
 * @param {string} props.variant - 'header' o 'footer' para diferentes estilos
 * @param {string} props.className - Clases CSS adicionales
 * @param {Function} props.onItemClick - Callback cuando se hace click en un item
 * @param {boolean} props.showIcons - Si debe mostrar los iconos
 * @param {string} props.activeSection - Sección activa actual
 * @param {Array} props.itemsToShow - Array de IDs de items a mostrar (si no se pasa, muestra todos)
 * @param {boolean} props.animateOnMount - Si debe animar los items al montar el componente
 * @param {number} props.animationDelay - Delay base para las animaciones
 */
const NavBar = ({ 
    variant = "header", 
    className = "", 
    onItemClick = null,
    showIcons = false, // Cambiado a false por defecto
    activeSection = "",
    itemsToShow = null,
    animateOnMount = true,
    animationDelay = 0.4
}) => {
    // Filtrar items si se especifica itemsToShow
    const items = itemsToShow 
        ? navItems.filter(item => itemsToShow.includes(item.id))
        : navItems;

    const handleClick = (e, item) => {
        e.preventDefault(); // Prevenir comportamiento por defecto
        
        if (onItemClick) {
            onItemClick(e, item.id);
        } else {
            // Comportamiento por defecto: scroll suave
            const element = document.querySelector(item.href);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }
    };

    // Estilos según variante
    const getItemClasses = (isActive) => {
        if (variant === "header") {
            return `group relative px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-300 ${
                isActive
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
            }`;
        } else if (variant === "footer") {
            return `text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200 font-medium ${
                isActive ? "text-blue-500 dark:text-blue-400" : ""
            }`;
        }
        return "";
    };

    const getIconClasses = (isActive) => {
        return `w-4 h-4 transition-transform duration-300 ${
            isActive ? "scale-110" : "group-hover:scale-110"
        }`;
    };

    if (variant === "header") {
        return (
            <nav className={`flex items-center gap-1 ${className}`}>
                {items.map((item, index) => {
                    const IconComponent = item.icon;
                    const isActive = activeSection === item.id;

                    return (
                        <motion.button
                            key={item.id}
                            onClick={(e) => handleClick(e, item)}
                            initial={animateOnMount ? { opacity: 0, y: -10 } : false}
                            animate={animateOnMount ? { opacity: 1, y: 0 } : {}}
                            transition={animateOnMount ? { delay: animationDelay + index * 0.05 } : {}}
                            className={getItemClasses(isActive)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="button"
                        >
                            {showIcons && IconComponent && (
                                <IconComponent className={getIconClasses(isActive)} />
                            )}
                            <span>{item.label}</span>

                            {/* Active indicator - línea inferior discreta */}
                            {isActive && (
                                <motion.div
                                    layoutId="activeIndicator"
                                    className="absolute left-0 right-0 bottom-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full"
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                />
                            )}
                        </motion.button>
                    );
                })}
            </nav>
        );
    }

    if (variant === "footer") {
        return (
            <nav className={`flex flex-wrap justify-center gap-6 text-sm ${className}`}>
                {items.map((link, index) => (
                    <motion.button
                        key={link.id}
                        onClick={(e) => handleClick(e, link)}
                        whileHover={{ scale: 1.05, y: -2 }}
                        className={getItemClasses(activeSection === link.id)}
                        initial={animateOnMount ? { opacity: 0, y: 20 } : false}
                        whileInView={animateOnMount ? { opacity: 1, y: 0 } : {}}
                        viewport={{ once: true }}
                        transition={animateOnMount ? { duration: 0.6, delay: animationDelay } : {}}
                        type="button"
                    >
                        {link.label}
                    </motion.button>
                ))}
            </nav>
        );
    }

    return null;
};

export default NavBar;
